import React, { useEffect, useState, useCallback } from 'react';
import { Clock, Search, Plus } from 'lucide-react';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import SeverityBadge from './SeverityBadge';
import Badge from '../common/Badge';
import { statusVariant } from '../../utils/statusVariant';
import CreateIncidentModal from './CreateIncidentModal';
import IncidentTriageModal from './IncidentTriageModal';
import { incidentsApi, Incident } from '../../services/incidentService';
import { usePermission } from '../../contexts/PermissionContext';

const ACT_TRIAGE = 'IRG_INCIDENT_TRIAGE';

const IncidentTriage = () => {
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_TRIAGE, 'view');
    const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [incidents, setIncidents] = useState<Incident[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const reload = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const data = await incidentsApi.list({ archived: false, limit: 200 });
            setIncidents(data);
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load' });
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => { setPage(1); }, [search, pageSize]);

    // Triage list keeps every incident that has not been satisfactorily closed.
    // Backend's `archived=false` already returns Open + CAPA Assigned + Task Assigned + In Progress.
    // 'In Progress' is kept for back-compat with rows that pre-date the 'Task Assigned' rename.
    const ACTIVE_TRIAGE_STATUSES = new Set(['Open', 'CAPA Assigned', 'Task Assigned', 'In Progress']);
    const filtered = incidents.filter(i => {
        if (!ACTIVE_TRIAGE_STATUSES.has(i.remediation_status)) return false;
        if (search && !i.incident_description.toLowerCase().includes(search.toLowerCase())
            && !i.incident_code.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const fmtAge = (created_on?: string) => {
        if (!created_on) return '—';
        const ms = Date.now() - new Date(created_on).getTime();
        const hrs = Math.floor(ms / 3600000);
        if (hrs < 1) return 'just now';
        if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
        const d = Math.floor(hrs / 24);
        return `${d} day${d === 1 ? '' : 's'} ago`;
    };

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to incident triage." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">

            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search incidents…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_incident_triage_search"
                    />
                    <Button variant="fill" icon={Plus} onClick={() => setShowCreateModal(true)} data-testid="BTN_incident_triage_create">Create</Button>
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Description</th>
                                        <th className="table-list-th">Type</th>
                                        <th className="table-list-th">Severity</th>
                                        <th className="table-list-th">Age</th>
                                        <th className="table-list-th">Reporter</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'xs', 'md', 'pill', 'pill', 'xs', 'sm', 'pill']} />}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={8} className="table-list-cell">
                                                <NoDataFound
                                                    title={search ? 'No Matching Incidents' : 'No Incidents Needing Triage'}
                                                    message={search ? 'No incidents match your search.' : 'All caught up! No incidents awaiting triage.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((incident, idx) => (
                                        <tr
                                            key={incident.id}
                                            className="table-list-row"
                                            onClick={() => setSelectedIncident(incident)}
                                            data-testid={`BTN_incident_triage_row_${incident.id}_open`}
                                        >
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell">
                                                {incident.code
                                                    ? <span className="cell-code">{incident.code}</span>
                                                    : <span className="text-muted text-xs">—</span>}
                                            </td>
                                            <td className="table-list-cell table-list-cell--truncate" title={incident.incident_description}>
                                                <span className="table-list-cell-title">
                                                    {incident.incident_description.split('\n')[0].slice(0, 80)}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                    {incident.occurrence_type || 'Event'}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                <SeverityBadge severity={incident.occurrence_type === 'Near Miss' ? 'Low' : 'Medium'} />
                                            </td>
                                            <td className="table-list-cell">
                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Clock size={12} />{fmtAge(incident.created_on)}
                                                </span>
                                            </td>
                                            <td className="table-list-cell">
                                                {incident.reported_by_user_name
                                                    || (incident.reported_by_user_id
                                                        ? `User #${incident.reported_by_user_id}`
                                                        : 'Anonymous')}
                                            </td>
                                            <td className="table-list-cell">
                                                <Badge variant={statusVariant(incident.remediation_status)} size="sm">
                                                    {incident.remediation_status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            recordLabel="incidents"
                        />
                    </div>
                </div>
            </div>

            <IncidentTriageModal
                isOpen={!!selectedIncident}
                incident={selectedIncident}
                onClose={() => setSelectedIncident(null)}
                onUpdated={() => {
                    setSelectedIncident(null);
                    setToast({ type: 'success', message: 'Incident triaged successfully.' });
                    reload();
                }}
            />

            <CreateIncidentModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => {
                    setShowCreateModal(false);
                    setToast({ type: 'success', message: 'Incident created successfully.' });
                    reload();
                }}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default IncidentTriage;
